import { updateInvestorData } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { getMainAddress } from '@dealstack/frontend/shared/address';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { JOIN_ALLOCATION_PERSONAL_INFO_FORM } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPersonalInformation/useJoinAndAllocationPersonalInformationController';
import { normalizePhoneNumberForBE } from '@dealstack/shared/lib/format/string';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	SAVE_SKIPPED,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { useDispatch } from 'react-redux';
import { userNameUpdated } from '@dealstack/frontend/shared/mechanism/redux/reducers/user/user';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import { getFormValuesFromInvestorData } from '@dealstack/frontend/investor/pages/Investment/common/form.helper.ts';

export enum INVESTMENT_LEAVE_PERSONAL_INFO_FORM {
	FIRST_NAME = 'firstName',
	LAST_NAME = 'lastName',
	BIRTH_DATE = 'birthDate',
	COUNTRY = 'country',
	CITY = 'city',
	POST_CODE = 'postCode',
	STREET = 'street',
	HOUSE_NUMBER = 'houseNumber',
	TELEPHONE_PREFIX = 'telephonePrefix',
	PHONE_NUMBER = 'phoneNumber',
}

interface PersonalInfoForm {
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.FIRST_NAME]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.LAST_NAME]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.BIRTH_DATE]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.COUNTRY]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.CITY]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.POST_CODE]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.STREET]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.HOUSE_NUMBER]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.PHONE_NUMBER]: string;
}

export const useInvestmentLeavePersonalInformationController = ({
	setIsContinueDisabled,
	setIsLoading,
}: {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
}) => {
	const tInvestor = useTranslationInvestor();
	const dispatch = useDispatch();
	const commonYup = useCommonYupValidation();

	const updateInvestorDataRequest = useWrappedRequest(updateInvestorData);
	const getInvestorDataRequest = useInvestor();

	const personalInformationForm = useForm<PersonalInfoForm>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			Yup.object().shape({
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.FIRST_NAME]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.LAST_NAME]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.BIRTH_DATE]: commonYup.date.past.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.COUNTRY]: commonYup.string.required.nullable(),
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.CITY]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.POST_CODE]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.STREET]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.HOUSE_NUMBER]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: commonYup.string.required,
				[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.PHONE_NUMBER]: commonYup.phone.required,
			})
		),
	});

	useEffect(() => {
		const payload = getInvestorDataRequest.data;
		if (!payload) {
			return;
		}

		const formValues = getFormValuesFromInvestorData(
			payload,
			INVESTMENT_LEAVE_PERSONAL_INFO_FORM
		);

		personalInformationForm.reset(formValues);
		void personalInformationForm.trigger().then(() => personalInformationForm.clearErrors());
	}, [getInvestorDataRequest.data]);

	const { isValid } = personalInformationForm.formState;

	useEffect(() => {
		setIsContinueDisabled(!isValid);

		return () => {
			setIsContinueDisabled(false);
		};
	}, [isValid]);

	const onSubmit = (formValues: PersonalInfoForm): Promise<WizardSaveResult> =>
		new Promise((resolve) => {
			setIsLoading(true);

			const phoneNumber = normalizePhoneNumberForBE(
				formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.PHONE_NUMBER],
				formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]
			);

			const formFirstName = formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.FIRST_NAME];
			const formLastName = formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.LAST_NAME];

			updateInvestorDataRequest
				.fetch({
					firstName: formFirstName,
					lastName: formLastName,
					birthDate: formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.BIRTH_DATE],
					addressId:
						getMainAddress(getInvestorDataRequest?.data?.person?.addresses)?.id ?? null,
					country: formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.COUNTRY],
					city: formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.CITY],
					postCode: formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.POST_CODE],
					street: formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.STREET],
					houseNumber: formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.HOUSE_NUMBER],
					telephonePrefix:
						formValues[INVESTMENT_LEAVE_PERSONAL_INFO_FORM.TELEPHONE_PREFIX],
					phoneNumber,
				})
				.onAnyError(() => {
					resolve(SAVE_FAILED);
					setIsLoading(false);
				})
				.onLocalError(() => {
					showToastV2({
						title: tInvestor('investment.toast.failed_save_personal_information'),
						state: 'error',
					});
				})
				.onSuccess(async () => {
					await getInvestorDataRequest.refetch();

					const isNameChanged =
						formFirstName !== getInvestorDataRequest?.data?.user?.firstName ||
						formLastName !== getInvestorDataRequest?.data?.user?.lastName;

					if (isNameChanged) {
						dispatch(
							userNameUpdated({
								firstName: formFirstName,
								lastName: formLastName,
							})
						);
					}

					showToastV2({ title: tInvestor('investment.toast.data_saved') });
					resolve(SAVE_OK);
					setIsLoading(false);
				});
		});

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			return SAVE_SKIPPED;
		}
		const isValid = await personalInformationForm.trigger();
		if (!isValid) {
			return SAVE_INVALID;
		}
		return onSubmit(personalInformationForm.getValues());
	});

	return {
		personalInformationForm,
		onSubmit,
	};
};
